// Third-party
import get from 'lodash/get';
import round from 'lodash/round';
import uniq from 'lodash/uniq';

import ComparisonHolding from '@/types/comparison-holding';
import ComparisonHoldingType from '@/types/comparison-holding-type';
import { isCapitalGainsTaxableHoldingType, extractCurrentPriceFromNav } from '@capintel/instruments-lib';

// Utils
import { formatMessage, formatMessageWithLocale, LOCALE } from './i18n';
import { generateBSONId, generateTempId } from './random';
import { isValidBase64 } from '@/utils/regex';
import { PILL_COLORS } from './colors';
import { riskRatingLiteralToAllocations } from './risk';
import { uniqueArray } from './objects';
import { convertShelvesToShelfStatuses } from '@/utils/shelf-management';
import { PERSISTENCE_KEYS } from '@/utils/persistence';

// Types
import Dictionary from '@/types/dictionary';
import Portfolio from '@/types/portfolio';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Benchmark from '@/types/benchmark';
import PortfolioType from '@/types/portfolio-type';
import PortfolioHolding from '@/types/portfolio-holding';
import CustomYieldParams from '@/types/custom-yield-params';
import ShelvableInstrument from '@/types/shelvable-instrument';
import CompliantInstrument from '@/types/compliant-instrument';
import RiskRating from '@/types/risk-rating';
import { CustomProxyMapping } from '@/types/app-config';
import { PortfolioFDC3, PositionFDC3, ReducedPortfolioFDC3 } from '@/types/portfolio-fdc3';
import { FDC3_BONDS_TYPE, FDC3_CASH_TYPE, FDC3_EQUITY_TYPE, FDC3_FUNDS_TYPE } from '@/enums/PortfolioFDC3';
import { InstrumentsByCodeType, InstrumentType } from '@/types/instrument-by-code';
import { INPUT_STRATEGY, INSTRUMENT_TYPE } from '@/enums/InstrumentByCode';
import LocalizedString from '@/types/localized-string';
import Alt from '@/types/models/alt';
import User from '@/types/user';
import AppRegion from '@/types/app-region';
import { HoldingAllocationMode } from '@/enums/Portfolio';
import Shelf from '@/types/shelf';

export enum HOLDING_TYPES {
  FUND = 'fund',
  STOCK = 'stock',
  CUSTOM_YIELD = 'customYield',
  BENCHMARK = 'benchmark',
  ALT = 'alt',
}

export enum PORTFOLIO_TYPES {
  TEMPLATE = 'TEMPLATE',
  CLIENT = 'CLIENT',
  UNASSIGNED = 'UNASSIGNED',
  DELETED = 'DELETED',
  FIRM = 'FIRM',
  CUSTOM = 'CUSTOM',
}

/** Fallback portfolio size ($100k) used when a comparison item has no dollar size — e.g. standalone instruments added to a comparison. Ensures dollar-based calculations (weighted averages, MER) produce valid results. */
export const DEFAULT_PORTFOLIO_SIZE = 100000;

export type PortfolioData = {
  funds: Fund[];
  stocks: Stock[];
  alts: Alt[];
  benchmarks: Benchmark[];
};

export enum ROLLING_INTERVALS {
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
}

export enum PortfolioEditorContext {
  COMPARISON = 'comparison',
  PORTFOLIO = 'portfolio',
}

/**
 * Sorts an array of portfolios alphabetically.
 *
 * @param {Portfolio[]} portfolios Array of portfolios to be sorted.
 *
 * @returns {Portfolio[]} Sorted array of portfolios.
 */
export const sortPortfolios = (portfolios: Portfolio[]) =>
  portfolios.sort((a, b) => {
    const sortingFields = ['name', 'contact.name'];

    for (const field of sortingFields) {
      const valA = get(a, field, '').toString().toLowerCase();
      const valB = get(b, field, '').toString().toLowerCase();

      if (valA < valB) {
        return -1;
      }

      if (valA > valB) {
        return 1;
      }
    }
    return 0;
  });

/**
 * Formats a portfolio for POST /portfolios request.
 *
 * @param {Portfolio} portfolio Portfolio to be formatted.
 * @returns {any} Formatted portfolio.
 */
export const formatPortfolioForSave = (portfolio: Portfolio) => {
  // Filter keys that are needed
  const portfolioKeys: (keyof Portfolio)[] = [
    '_id',
    'correlationId',
    'name',
    'size',
    'holdings',
    'discretionaryMer',
    'currency',
    'mine',
    'contact',
    'enterpriseGroups',
    'enterpriseId',
    'sharedWith',
    'codes',
    'mer',
    'riskProfileRef',
    'riskProfile',
    'dates',
    'allocationMode',
    'metadata',
    'planning',
    'isFromPostMessage',
  ];

  const holdingKeys: (keyof PortfolioHolding)[] = [
    '_id',
    'symbol',
    'name',
    'original',
    'weight',
    'units',
    'dollars',
    'currency',
    'proxyKey',
    'universe',
    'taxSettings',
    'lots',
  ];

  const formattedPortfolio: Partial<Portfolio> = Object.fromEntries(
    portfolioKeys.map(key => [
      key,
      key === 'holdings' ? portfolio[key].map(holding => Object.fromEntries(holdingKeys.map(holdingKey => [holdingKey, holding[holdingKey]]))) : portfolio[key],
    ]),
  );

  return {
    ...formattedPortfolio,
    calculatedAt: new Date(),
  };
};

/**
 * Helper function to get holdings of a specific type from a portfolio
 *
 * @param {Portfolio} portfolio Portfolio to use
 * @param {HOLDING_TYPES} holdingType Type of holdings to filter
 * @return {PortfolioHolding[]} Filtered holdings of the specified type
 */
export const getHoldingsByType = (portfolio: Portfolio, holdingType: HOLDING_TYPES): PortfolioHolding[] =>
  portfolio.holdings.filter(h => h.original && h.original.type === holdingType);

/**
 * Split portfolio holdings into stocks and funds
 *
 * @param {Portfolio} portfolio Portfolio to use.
 * @return {[Fund[], Stock[]]} Arrays of funds and stocks
 */
export const splitPortfolioHoldings = (portfolio: Portfolio) => {
  const funds = getHoldingsByType(portfolio, HOLDING_TYPES.FUND);
  const stocks = getHoldingsByType(portfolio, HOLDING_TYPES.STOCK);
  const alts = getHoldingsByType(portfolio, HOLDING_TYPES.ALT);
  const customYields = getHoldingsByType(portfolio, HOLDING_TYPES.CUSTOM_YIELD);

  return [funds, stocks, alts, customYields];
};

/**
 * Validates if a portfolio is a model portfolio.
 *
 * @param {Portfolio} portfolio Portfolio to validate.
 * @return {boolean} `true` if portfolio is a model; `false` otherwise.
 */
export const portfolioIsModel = (portfolio: Portfolio) => portfolio._id && !portfolio._id.startsWith('new-') && portfolio.mine && !portfolio.contact;

/**
 * Validates if a portfolio is a client portfolio.
 *
 * @param {Portfolio} portfolio Portfolio to validate.
 * @return {boolean} `true` if portfolio is for a client; `false` otherwise.
 */
export const portfolioIsClient = (portfolio: Portfolio) => portfolio._id && !portfolio._id.startsWith('new-') && !portfolio.mine && portfolio.contact;

/**
 * Validates if a portfolio has empty holdings.
 *
 * @param {Portfolio} portfolio Portfolio to validate.
 * @return {boolean} `true` if portfolio has no holdings or empty holdings array; `false` otherwise.
 */
export const hasEmptyHoldings = (portfolio: Portfolio) => !portfolio.holdings || portfolio.holdings.length === 0;

/**
 * Returns the portfolio type (Template, Client, Unassigned)
 *
 * @param {Portfolio} portfolio Portfolio to use.
 * @return {string} Returns portfolio type as a string.
 */
export const getPortfolioType = (portfolio: Portfolio) => {
  if (portfolio.enterprise) {
    return PORTFOLIO_TYPES.FIRM;
  }

  if (portfolioIsModel(portfolio)) {
    return PORTFOLIO_TYPES.TEMPLATE;
  }

  if (portfolioIsClient(portfolio)) {
    return PORTFOLIO_TYPES.CLIENT;
  }

  return PORTFOLIO_TYPES.UNASSIGNED;
};

/**
 * Get the color associated with a portfolio type, if any. To be used when elements are colored based
 * on the portfolio type (ie: Comparison card pill label).
 *
 * @param portfolio Portfolio type to use.
 * @return {string} Returns portfolio type as a string.
 */
export const getPortfolioTypeColor = (portfolio: PortfolioType) => {
  if (portfolio === PORTFOLIO_TYPES.FIRM) {
    return PILL_COLORS.BLUE;
  }

  if (portfolio === PORTFOLIO_TYPES.TEMPLATE) {
    return PILL_COLORS.TEAL;
  }

  if (portfolio === PORTFOLIO_TYPES.CLIENT) {
    return PILL_COLORS.VIOLET;
  }

  if (portfolio === PORTFOLIO_TYPES.CUSTOM) {
    return PILL_COLORS.BLUE;
  }

  return PILL_COLORS.GREY;
};

/**
 * Fetch the data for a specific portfolio holding within the portfolio's data.
 *
 * @param {PortfolioHolding} holding Portfolio holding.
 * @return {PortfolioData} Funds, stocks and benchmarks data for the portfolio.
 */
export const getPortfolioHoldingData = (holding: PortfolioHolding, data: PortfolioData) => {
  switch (holding.original.type) {
    case HOLDING_TYPES.FUND:
    case HOLDING_TYPES.CUSTOM_YIELD: {
      return data.funds.find(f => f._id === holding.original.id);
    }
    case HOLDING_TYPES.STOCK: {
      return data.stocks.find(s => s._id === holding.original.id);
    }
    case HOLDING_TYPES.ALT: {
      return data.alts.find(a => a._id === holding.original.id);
    }
    default:
      return undefined;
  }
};

/**
 * Fetch the youngest holding out of a portfolio
 *
 * @param {Portfolio} portfolio Portfolio.
 * @param {PortfolioData} data Data for the portfolio.
 * @return Object containing the symbol and date of the youngest holding.
 */
export const getPortfolioYoungestHolding = (portfolio: Portfolio, data: PortfolioData) => {
  let youngestHolding = null;

  for (const holding of portfolio.holdings) {
    const holdingData = getPortfolioHoldingData(holding, data);

    if (!holdingData) {
      return null;
    }

    const monthlyRolling = holdingData.performance.rolling.monthly;

    if (!monthlyRolling || !monthlyRolling.length) {
      return null;
    }

    if (!youngestHolding || monthlyRolling[0][0] > youngestHolding.date) {
      youngestHolding = {
        symbol: holding.symbol,
        name: holding.name,
        date: monthlyRolling[0][0],
      };
    }
  }

  return youngestHolding;
};

/**
 * Get the total portfolio allocation
 *
 * @param {Portfolio} portfolio Portfolio.
 * @return {number} Portfolio allocation
 */
export const getTotalPortfolioAllocation = (portfolio: Portfolio) => {
  if (!portfolio.size) {
    return 0;
  }

  const totalWeight = portfolio.holdings.reduce((total, holding) => total + holding.weight, 0);
  return round(totalWeight, 2);
};

/**
 * Determine if portfolio holdings allocations have changed (both in size and content)
 *
 * @param {PortfolioHolding[]} oldHoldings old Portfolio holdings
 * @param {PortfolioHolding[]} newHoldings new Portfolio holdings
 * @return {boolean} true if holdings list is different in size of composition (holdings and their weight)
 */
export const hasAllocationsChanged = (oldHoldings: PortfolioHolding[], newHoldings: PortfolioHolding[]) => {
  const oldHoldingsArray = oldHoldings.filter(el => el.weight && el.weight > 0);
  const newHoldingsArray = newHoldings.filter(el => el.weight && el.weight > 0);

  if (oldHoldingsArray.length !== newHoldingsArray.length) {
    return true;
  }

  return !oldHoldingsArray.every(oldHolding =>
    newHoldingsArray.some(newHolding => newHolding._id === oldHolding._id && newHolding.weight === oldHolding.weight),
  );
};

/**
 * Evaluate the allocation mode of a portfolio.
 * If the portfolio has units set on any of its holdings, use the units as the allocation mode.
 * @param portfolio Portfolio to evaluate
 */
export const portfolioUsesUnits = (portfolio: Portfolio): boolean => portfolio.holdings.some(holding => holding.units != null);

/**
 * Set a default allocation mode if the portfolio.allocationMode is not defined
 * @param portfolio Portfolio to evaluate
 */
export const setDefaultAllocationMode = (portfolio: Portfolio): Portfolio => {
  const newPortfolio = { ...portfolio };
  if (newPortfolio.allocationMode == null) {
    newPortfolio.allocationMode = portfolioUsesUnits(newPortfolio) ? HoldingAllocationMode.Units : HoldingAllocationMode.Weight;
  }
  return newPortfolio;
};

/**
 * Determine if portfolio is new
 *
 * @param {Portfolio} portfolio Portfolio to check
 * @return {boolean} true if portfolio is new, otherwise false
 */
export const portfolioIsNew = (portfolio: Portfolio) => portfolio._id === undefined || (portfolio._id !== undefined && portfolio._id.startsWith('new-'));

export const generateCustomYieldHolding = (
  config: CustomYieldParams,
  currency: string,
  shelfInfo: ShelvableInstrument & CompliantInstrument,
): PortfolioHolding => {
  const holding: PortfolioHolding = {
    _id: generateBSONId(),
    currency,
    original: {
      id: generateBSONId(),
      type: HOLDING_TYPES.CUSTOM_YIELD,
      yieldParams: config,
    },
    symbol: formatMessage(`Common.${config.type}`),
    name: {
      [LOCALE.EN_CA]: config.name,
      [LOCALE.FR_CA]: config.name,
    },
    weight: 0,
    dollars: 0,
    flags: uniq([...convertShelvesToShelfStatuses(shelfInfo.filteredShelves as Shelf[], shelfInfo.complianceComparisons)]),
    filteredShelves: shelfInfo.filteredShelves,
    complianceComparisons: shelfInfo.complianceComparisons,
  };

  return holding;
};

/**
 * Generates an allocation-based risk rating for the portfolio based on the underlying holdings (ie: 30% Low risk, 70% Medium risk).
 * @param portfolio Portfolio to evaluate the risk rating.
 * @param data Data of the portfolio's holdings
 */
export const getPortfolioRiskRating = (portfolio: Portfolio, data?: PortfolioData): RiskRating | undefined => {
  if (!data) {
    return undefined;
  }

  return portfolio.holdings.reduce((acc, holding) => {
    if (holding.original.type !== HOLDING_TYPES.FUND) {
      return acc;
    }

    const holdingData = getPortfolioHoldingData(holding, data) as Fund | undefined;
    if (!holdingData) {
      return acc;
    }

    const ratings = riskRatingLiteralToAllocations(holdingData.riskRating);
    if (!ratings) {
      return acc;
    }

    const keys = uniqueArray(Object.keys(acc).concat(Object.keys(ratings)));

    for (const key of keys) {
      if (!acc[key]) {
        acc[key] = 0;
      }

      const newVal = ((ratings[key] || 0) * holding.weight) / 100;
      acc[key] += newVal;
    }

    return acc;
  }, {} as RiskRating);
};

/**
 * Generates an allocation-based risk asset type for the portfolio based on the underlying holdings (ie: 30% EQUITY, 70% INCOME).
 * @param portfolio Portfolio to evaluate the risk asset type.
 * @param data Data of the portfolio's holdings
 */
export const getPortfolioRiskAssetType = (portfolio: Portfolio, data?: PortfolioData): Dictionary<number> => {
  if (!data) {
    return {};
  }

  return portfolio.holdings.reduce((acc, holding) => {
    let key: string | undefined;

    switch (holding.original.type) {
      case HOLDING_TYPES.FUND: {
        const holdingData = getPortfolioHoldingData(holding, data) as Fund | undefined;
        if (!holdingData) {
          return acc;
        }

        key = holdingData.riskAssetType as string | undefined;
        break;
      }
    }

    if (!key) {
      return acc;
    }

    if (!acc[key]) {
      acc[key] = 0;
    }

    acc[key] += holding.weight;

    return acc;
  }, {} as Dictionary<number>);
};

export const transformFDC3Positions = (fdc3Positions: PositionFDC3[], size: number, customProxyMapping?: CustomProxyMapping[]): InstrumentsByCodeType => {
  const bondTypes: string[] = Object.values(FDC3_BONDS_TYPE);
  const fundTypes: string[] = Object.values(FDC3_FUNDS_TYPE);
  const equityTypes: string[] = Object.values(FDC3_EQUITY_TYPE);
  const cashTypes: string[] = Object.values(FDC3_CASH_TYPE);

  const instrumentInput: InstrumentsByCodeType = {
    instruments: [],
    allocationMode: HoldingAllocationMode.Weight,
    size,
    inputStrategy: INPUT_STRATEGY.TYPE_STRATEGY,
  };

  fdc3Positions.forEach(position => {
    const fdsType = position.instrument.id.FDS_TYPE;
    const weight = !position.weight || position.weight < 0 ? 0 : position.weight;

    const instrument: InstrumentType = {
      weight: weight * 100,
      identifier: position.instrument.id.FDS_ID,
      type: INSTRUMENT_TYPE.PROXY,
      name: position.instrument.name,
    };

    if (instrument.identifier && equityTypes.includes(fdsType)) {
      instrument.type = INSTRUMENT_TYPE.STOCK;
    } else if (instrument.identifier && fundTypes.includes(fdsType)) {
      instrument.type = INSTRUMENT_TYPE.FUND;
    } else if (instrument.identifier && bondTypes.includes(fdsType)) {
      instrument.type = INSTRUMENT_TYPE.BOND;
    } else if (cashTypes.includes(fdsType) && position.instrument.id.FDS_INPUT_ID && position.instrument.id.FDS_INPUT_ID.startsWith('CASH_')) {
      instrument.type = INSTRUMENT_TYPE.CASH;
      instrument.identifier = position.instrument.id.FDS_INPUT_ID;
    }

    if (customProxyMapping?.length) {
      const proxyInstrument = customProxyMapping.find(customInstrument => position?.instrument?.id?.ticker === customInstrument.proxyRef);
      if (proxyInstrument) {
        instrument.type = proxyInstrument.type as INSTRUMENT_TYPE;
        instrument.identifier = proxyInstrument.id;
      }
    }

    instrumentInput.instruments.push(instrument);
  });

  return instrumentInput;
};

export const transformFDC3BasePortfolio = (fdc3Portfolio: PortfolioFDC3): Portfolio => {
  const tempId = generateBSONId();
  const convertedPortfolio: Portfolio = {
    _id: fdc3Portfolio.id.portfolioId,
    correlationId: tempId,
    name: {
      'en-CA': fdc3Portfolio.name,
      'en-US': fdc3Portfolio.name,
      'fr-CA': fdc3Portfolio.name,
    },
    size: fdc3Portfolio.marketValue,
    allocationMode: HoldingAllocationMode.Weight,
    availableActions: { read: true, clone: true, edit: false, delete: false },
    holdings: [],
    dates: {},
    isFromPostMessage: true,
  };

  return convertedPortfolio;
};

export const transformPortfoliosToFDC3Base = (portfolios: Portfolio[]): ReducedPortfolioFDC3[] => {
  const convertedFDC3BasePortfolios: ReducedPortfolioFDC3[] = portfolios.map(p => ({
    type: 'fdc3.portfolio',
    name: p.name['en-CA']!,
    id: {
      portfolioId: p._id!,
    },
  }));

  return convertedFDC3BasePortfolios;
};

/**
 * Determines if the given portfolio type represents a model portfolio.
 * Model portfolios include TEMPLATE and FIRM types.
 *
 * @param portfolioType - The portfolio type to check
 * @returns true if the portfolio type is TEMPLATE or FIRM, false otherwise
 */
export function isModelType(portfolioType?: PortfolioType): boolean {
  if (!portfolioType) {
    return false;
  }
  return [PORTFOLIO_TYPES.TEMPLATE, PORTFOLIO_TYPES.FIRM].includes(portfolioType as PORTFOLIO_TYPES);
}

/**
 * Get an empty portfolio based on the portfolio type.
 *
 * @param {PortfolioType} portfolioType Portfolio type.
 * @param {GenericContact} contact Contact to use for the portfolio.
 * @return {Portfolio} Empty portfolio.
 */
export const getEmptyPortfolio = (portfolioType: PortfolioType = PORTFOLIO_TYPES.UNASSIGNED): Portfolio => {
  // Setup general portfolio context with default values
  const tempId: string = generateTempId();
  const isModel: boolean = isModelType(portfolioType);
  const portfolioTypeTranslation: string = `PortfolioEditorBuilder.Overview.${isModel ? 'DefaultModelName' : 'DefaultPortfolioName'}`;
  const name: LocalizedString = {
    [LOCALE.EN_CA]: formatMessageWithLocale(portfolioTypeTranslation, LOCALE.EN_CA),
    [LOCALE.EN_US]: formatMessageWithLocale(portfolioTypeTranslation, LOCALE.EN_US),
    [LOCALE.FR_CA]: formatMessageWithLocale(portfolioTypeTranslation, LOCALE.FR_CA),
  };

  return {
    _id: tempId,
    correlationId: tempId.replace('new-', ''),
    name,
    holdings: [],
    size: 100000,
    dates: {},
    allocationMode: HoldingAllocationMode.Weight,
  } as Portfolio;
};

/**
 * Add portfolio holdings and si to respective portfolios
 *
 * @param portfolios A list of portfolios that we would like to add holdings to
 * @param portfoliosWithHoldings A list of portfolios with holdings that we would like to add to the `portfolios`
 * @returns The updated portfolios array with the holdings from `portfoliosWithHoldings` added to the respective portfolios
 */
export const addHoldingsToPortfolios = (portfolios: Portfolio[], portfoliosWithHoldings: Portfolio[]) =>
  portfolios.map(portfolio => {
    const portfolioWithHoldings = portfoliosWithHoldings.find(p => p._id === portfolio._id);
    return portfolioWithHoldings ? { ...portfolio, holdings: portfolioWithHoldings.holdings, size: portfolioWithHoldings.size } : portfolio;
  });

/**
 * Generates a localized name for the portfolio based on the user's app region.
 *
 * @param {name} name Name to use.
 * @param {User} user User to use.
 * @return {LocalizedString} Localized name for the portfolio.
 */
export const generateLocalizedNameForPortfolio = (name: string, user: User): LocalizedString => {
  const { appRegion } = user.appConfig;

  const localizedName: LocalizedString = {
    [LOCALE.EN_CA]: name,
    [LOCALE.FR_CA]: name,
    ...(appRegion === AppRegion.US && { [LOCALE.EN_US]: name }),
  };

  return localizedName;
};

/**
 * Removes multiple holdings from the given holdings dictionary.
 * @param holdings  Dictionary of holdings to remove from.
 * @param ids Array of holding IDs to remove.
 * @returns Updated dictionary of holdings.
 */
export const removeManyHoldings = (holdings: Dictionary<Fund | Stock>, ids: string[]) =>
  Object.values(holdings)
    .filter(holding => !ids.includes(holding._id))
    .reduce(
      (acc, holding) => ({
        ...acc,
        [holding._id]: holding,
      }),
      {},
    );

/**
 * Decodes a base64-encoded id for an existing client portfolio.
 *
 * If the input string is valid base64 and, when decoded, starts with the
 * expected prefix (`PERSISTENCE_KEYS.EXISTING_CLIENT_PORTFOLIO-`), the prefix
 * is removed and the remainder is returned. Otherwise, the original id is returned.
 *
 * @param id - The string id to decode.
 * @returns The decoded portfolio id without the prefix, or the original id if not applicable.
 */
export const decodeIdForExistingClientPortfolio = (id: string): string => {
  if (isValidBase64(id)) {
    const decodedValue = atob(id);
    const prefix = `${PERSISTENCE_KEYS.EXISTING_CLIENT_PORTFOLIO}-`;

    if (decodedValue.startsWith(prefix)) {
      return decodedValue.slice(prefix.length);
    }
    return id;
  }

  return id;
};

/**
 * Normalizes holding names for US locale in imported portfolios.
 * This is used when a US client is sending a portfolio that has holdings with only en-US names.
 * @param data Portfolio data to normalize holding names for US region.
 * @returns Normalized portfolio data with holding names adjusted for US region.
 */
export const normalizeHoldingNamesForUSLocale = (data: Portfolio): Portfolio => {
  if (data.holdings) {
    data.holdings.forEach((holding: PortfolioHolding) => {
      if (holding.name && holding.name[LOCALE.EN_US]) {
        if (!holding.name[LOCALE.EN_CA]) {
          holding.name[LOCALE.EN_CA] = holding.name[LOCALE.EN_US];
        }
        if (!holding.name[LOCALE.FR_CA]) {
          holding.name[LOCALE.FR_CA] = holding.name[LOCALE.EN_US];
        }
      }
    });
  }
  return data;
};

// Type guard for PortfolioHolding
function isPortfolioHolding(holding: PortfolioHolding | ComparisonHolding): holding is PortfolioHolding {
  return (holding as PortfolioHolding).original !== undefined;
}

// Utility to get the holding type
//  Overloads for getHoldingType are required to support all usages(Comparison and Portfolio) in the codebase:
//  Accepting either PortfolioHolding, ComparisonHolding, or their union type.
//  TypeScript needs explicit overloads for both individual types and the union,
//  otherwise it cannot resolve calls where the argument is PortfolioHolding | ComparisonHolding.
//  This avoids type errors when passing union types and ensures correct type inference.
function getHoldingType(holding: PortfolioHolding): ComparisonHoldingType | undefined;
function getHoldingType(holding: ComparisonHolding): ComparisonHoldingType | undefined;
function getHoldingType(holding: PortfolioHolding | ComparisonHolding): ComparisonHoldingType | undefined; // Overload signature for declaration of type checking
// Implementation signature
function getHoldingType(holding: PortfolioHolding | ComparisonHolding): ComparisonHoldingType | undefined {
  if (isPortfolioHolding(holding)) {
    return holding.original?.type as ComparisonHoldingType | undefined;
  }
  return holding.type;
}

// Helper function to check if a cost basis value is invalid
function isInvalidCostBasis(costBasis: number | null | undefined): boolean {
  return costBasis === null || costBasis === undefined || costBasis === 0;
}

// Determines if a holding is missing cost basis.
// Returns false for non-taxable holdings or if no current price is available.
// Returns true if cost basis is missing for taxable holdings.
// If lots exist, validates that each lot has costBasis instead of holding-level costBasis.
export function isHoldingMissingCostBasis(holding: PortfolioHolding | ComparisonHolding | undefined, assetType: LocalizedString | undefined): boolean {
  if (!holding) return true;

  const holdingType = getHoldingType(holding);
  if (!holdingType || !isCapitalGainsTaxableHoldingType(holdingType, assetType)) return false;
  if (extractCurrentPriceFromNav(holding.nav) === undefined) return false;

  // If lots exist, validate lots instead of holding-level costBasis
  if (holding.lots && Array.isArray(holding.lots) && holding.lots.length > 0) {
    // Check that all lots have valid costBasis
    return holding.lots.some(lot => isInvalidCostBasis(lot.costBasis));
  }

  // For holdings without lots, check holding-level costBasis
  if (!holding.taxSettings) return true;

  return isInvalidCostBasis(holding.taxSettings.costBasis);
}

// Returns true if any holding in the array is missing cost basis.
export function portfolioHasMissingCostBasis(
  holdings: (PortfolioHolding | ComparisonHolding)[] = [],
  getAssetType?: (holding: PortfolioHolding | ComparisonHolding) => LocalizedString | undefined,
): boolean {
  return holdings.some(holding => {
    // First try to get assetType from the holding itself (comparison context)
    let assetType: LocalizedString | undefined;
    if ('assetType' in holding && holding.assetType) {
      assetType = holding.assetType;
    }

    // If not on holding, try to get from getter function (portfolio editor context)
    if (!assetType && getAssetType) {
      assetType = getAssetType(holding);
    }

    return isHoldingMissingCostBasis(holding, assetType);
  });
}

export function isInputDisabledForCostBasis(holding: PortfolioHolding, assetType?: LocalizedString): boolean {
  // Disable holding-level cost basis if lots exist
  if (holding.lots && Array.isArray(holding.lots) && holding.lots.length > 0) {
    return true;
  }

  // Only care about taxable holdings
  if (!isCapitalGainsTaxableHoldingType(holding.original.type, assetType)) {
    return true;
  }

  // For taxable funds, disable if nav is missing
  if (holding.original.type === HOLDING_TYPES.FUND && extractCurrentPriceFromNav(holding.nav) === undefined) {
    return true;
  }
  // Otherwise, keep the cost basis input enabled
  return false;
}
